/**
 * โหมดมืด/สว่างของธีม glass — `<html data-scheme="light|dark">`
 * ค่าแรกตั้งโดย inline script ใน index.html (ก่อนวาดหน้าแรก) · logic ล้วนอยู่ใน colorSchemeCore.ts
 * ธีม classic: data-scheme ไม่มีผล (CSS กลางวันผูก html[data-ui="glass"][data-scheme="light"] เท่านั้น)
 */
import { useSyncExternalStore } from 'react'
import { SCHEME_STORAGE_KEY, bangkokHour, parseScheme, resolveScheme, type ColorScheme } from './colorSchemeCore'

export type { ColorScheme } from './colorSchemeCore'

type SchemeState = { scheme: ColorScheme; auto: boolean }

const DARK_QUERY = '(prefers-color-scheme: dark)'
const listeners = new Set<() => void>()
let state: SchemeState | null = null
let syncStarted = false
/**
 * ค่าที่ผู้ใช้เลือกเก็บในหน่วยความจำ เมื่อ localStorage เขียน/ลบไม่ได้ (private mode / quota)
 * null = ไม่ใช้ (อ่านจาก localStorage) · { s } = ใช้ค่านี้แทน (s = null คือกลับอัตโนมัติ) จนปิดหน้า
 */
let memChoice: { s: ColorScheme | null } | null = null

function readUserScheme(): ColorScheme | null {
  if (memChoice) return memChoice.s
  try {
    return parseScheme(localStorage.getItem(SCHEME_STORAGE_KEY))
  } catch {
    return null
  }
}

function deviceDark(): boolean {
  try {
    return window.matchMedia?.(DARK_QUERY).matches ?? false
  } catch {
    return false
  }
}

function resolveNow(): ColorScheme {
  return resolveScheme({ user: readUserScheme(), deviceDark: deviceDark(), hour: bangkokHour(Date.now()) })
}

/** ไม่มี data-scheme → ค่ากลางคืน (เหมือน CSS) */
export function getScheme(): ColorScheme {
  return parseScheme(document.documentElement.dataset.scheme) ?? 'dark'
}

function readState(): SchemeState {
  return { scheme: getScheme(), auto: readUserScheme() === null }
}

function getSnapshot(): SchemeState {
  if (!state) state = readState()
  return state
}

function emit() {
  const next = readState()
  if (!state || state.scheme !== next.scheme || state.auto !== next.auto) {
    state = next
    listeners.forEach((l) => l())
  }
}

function applyScheme(s: ColorScheme) {
  // ธีม Ground (พื้นหลังไหล) กำหนดโหมดมืด/สว่างตามชุดสีของมันเอง — ไม่ตามเวลา/เครื่อง/ปุ่มสลับ
  const groundTone = parseScheme(document.documentElement.dataset.groundTone === 'dark' ? 'dark' : document.documentElement.dataset.groundTone === 'light' ? 'light' : null)
  const next = groundTone ?? s
  if (document.documentElement.dataset.scheme !== next) document.documentElement.dataset.scheme = next
  emit()
}

/** ประเมินโหมดมืด/สว่างใหม่ (เช่น หลังออกจากธีม Ground) — ไม่แตะค่าที่ผู้ใช้เลือกไว้ */
export function refreshScheme(): void {
  applyScheme(resolveNow())
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const SERVER_STATE: SchemeState = { scheme: 'dark', auto: true }

export function useColorScheme(): SchemeState {
  return useSyncExternalStore(subscribe, getSnapshot, () => SERVER_STATE)
}

/** ผู้ใช้เลือกเอง — บันทึก oc-scheme แล้วใช้ทันที · คืน false ถ้าบันทึกไม่ได้ (จำในหน่วยความจำจนปิดหน้า) */
export function setUserScheme(s: ColorScheme): boolean {
  let saved = true
  try {
    localStorage.setItem(SCHEME_STORAGE_KEY, s)
    memChoice = null
  } catch {
    saved = false // private mode / quota
    memChoice = { s }
  }
  applyScheme(s)
  return saved
}

/** กลับเป็นอัตโนมัติ (เครื่อง + เวลาไทย) */
export function clearUserScheme(): void {
  try {
    localStorage.removeItem(SCHEME_STORAGE_KEY)
    memChoice = null
  } catch {
    memChoice = { s: null } // ลบไม่ได้ — กันค่าเก่าใน localStorage กลับมา
  }
  applyScheme(resolveNow())
}

/** ประเมินใหม่เมื่อกลับมาที่หน้า / เครื่องเปลี่ยนโหมด — เฉพาะโหมดอัตโนมัติ · ไม่ใช้ timer */
export function startAutoSchemeSync(): void {
  if (syncStarted) return
  syncStarted = true
  const resync = () => {
    if (readUserScheme() === null) applyScheme(resolveNow())
  }
  if (!parseScheme(document.documentElement.dataset.scheme)) applyScheme(resolveNow())
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') resync()
  })
  try {
    window.matchMedia?.(DARK_QUERY).addEventListener?.('change', resync)
  } catch {
    /* เบราว์เซอร์เก่า — ใช้ visibilitychange อย่างเดียว */
  }
  // แท็บอื่นเลือก/ล้าง oc-scheme (key null = localStorage.clear()) → แท็บนี้ตาม
  window.addEventListener('storage', (e) => {
    if (e.key === SCHEME_STORAGE_KEY || e.key === null) {
      memChoice = null // แท็บอื่นเลือกล่าสุด
      applyScheme(resolveNow())
    }
  })
}

/** สลับแบบเฟดด้วย View Transitions ถ้ามีและไม่ลดการเคลื่อนไหว — ไม่มี → สลับทันที */
export function applySchemeWithTransition(next: () => void): void {
  let reduce = false
  try {
    reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
  } catch {
    /* ignore */
  }
  const doc = document as Document & { startViewTransition?: (cb: () => void) => unknown }
  if (!reduce && typeof doc.startViewTransition === 'function') {
    doc.startViewTransition(next)
  } else {
    next()
  }
}
